import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FeatureProvider } from "@/lib/features";
import { BCMProvider } from "@/lib/bcm";
import { ThemeProvider } from "@/lib/theme";
import RoleGate from "@/components/app/RoleGate";

const ADMIN = ["super_admin", "hr_admin"];
const MANAGER = ["super_admin", "hr_admin", "manager", "team_lead", "department_head"];
import { registerSW } from "@/lib/push";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import People from "@/pages/People";
import PeopleImport from "@/pages/PeopleImport";
import Billing from "@/pages/Billing";
import PlansAdmin from "@/pages/PlansAdmin";
import PlatformBilling from "@/pages/PlatformBilling";
import Shifts from "@/pages/Shifts";
import Timesheets from "@/pages/Timesheets";
import TimesheetApprovals from "@/pages/TimesheetApprovals";
import EmployeeProfile from "@/pages/EmployeeProfile";
import ProfilePage from "@/pages/ProfilePage";
import OrgChart from "@/pages/OrgChart";
import Attendance from "@/pages/Attendance";
import Leaves from "@/pages/Leaves";
import Approvals from "@/pages/Approvals";
import Tasks from "@/pages/Tasks";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Inbox from "@/pages/Inbox";
import Onboarding from "@/pages/Onboarding";
import Offboarding from "@/pages/Offboarding";
import Assets from "@/pages/Assets";
import Documents from "@/pages/Documents";
import Announcements from "@/pages/Announcements";
import Messaging from "@/pages/Messaging";
import AIAssistant from "@/pages/AIAssistant";
import Settings from "@/pages/Settings";
import Organization from "@/pages/Organization";
import Holidays from "@/pages/Holidays";
import FeatureControls from "@/pages/FeatureControls";
import Payroll from "@/pages/Payroll";
import Meetings from "@/pages/Meetings";
import CalendarPage from "@/pages/CalendarPage";
import Helpdesk from "@/pages/Helpdesk";
import Claims from "@/pages/Claims";
import Performance from "@/pages/Performance";
import Recruitment from "@/pages/Recruitment";
import AuditLog from "@/pages/AuditLog";
import NotificationsPage from "@/pages/NotificationsPage";
import Marketing from "@/pages/Marketing";
import MasterPanel from "@/pages/MasterPanel";
import ResetPassword from "@/pages/ResetPassword";
import Signup from "@/pages/Signup";
import BackupRestore from "@/pages/BackupRestore";
import CommunicationSuite from "@/pages/CommunicationSuite";
import MeetingsVideo from "@/pages/MeetingsVideo";
import PerformancePro from "@/pages/PerformancePro";
import RecruitmentATS from "@/pages/RecruitmentATS";
import LMS from "@/pages/LMS";
import WorkflowStudio from "@/pages/WorkflowStudio";
import AICopilots from "@/pages/AICopilots";
import PlatformLayout from "@/components/app/PlatformLayout";
import {
  ClientHealth, UsageAnalytics, TenantUsers, DemoWorkspaces, ImpersonationLogs,
  PricingMgr, SubscriptionsMgr, InvoicesMgr, LicenseKeys, TrialMgmt,
  FeatureControlsMaster, WebsiteManager, EmailPdfTemplates,
  SystemHealth, ApiUsage, ErrorLogs, AuditLogsMaster,
  SupportTickets, ClientAnnouncements,
  PlatformSettings, AdminUsers,
  ModuleAccess, WidgetManager, StorageUsage, SecurityLogs,
  ClientRequests, RolesPermissions, Integrations,
} from "@/pages/platform/PlatformSkeletons";
import BrandContentManager from "@/pages/platform/BrandContentManager";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import AdminLogin from "@/pages/AdminLogin";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  useEffect(() => {
    // Eager service-worker registration so browser push can activate without waiting for an explicit "Enable push" click.
    registerSW().catch(() => {});
  }, []);
  return (
    <AuthProvider>
      <FeatureProvider>
        <BCMProvider>
        <ThemeProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="people" element={<RoleGate allow={MANAGER}><People /></RoleGate>} />
              <Route path="people/import" element={<RoleGate allow={ADMIN}><PeopleImport /></RoleGate>} />
              <Route path="billing" element={<RoleGate allow={ADMIN}><Billing /></RoleGate>} />
              <Route path="subscription" element={<RoleGate allow={ADMIN}><Billing /></RoleGate>} />
              <Route path="plans-admin" element={<RoleGate allow={ADMIN}><PlansAdmin /></RoleGate>} />
              <Route path="platform-billing" element={<RoleGate allow={["super_admin"]}><PlatformBilling /></RoleGate>} />
              <Route path="shifts" element={<RoleGate allow={ADMIN}><Shifts /></RoleGate>} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="timesheet-approvals" element={<RoleGate allow={MANAGER}><TimesheetApprovals /></RoleGate>} />
              <Route path="people/:id" element={<RoleGate allow={MANAGER}><EmployeeProfile /></RoleGate>} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="org-chart" element={<OrgChart />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="leaves" element={<Leaves />} />
              <Route path="approvals" element={<RoleGate allow={MANAGER}><Approvals /></RoleGate>} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="onboarding" element={<RoleGate allow={ADMIN}><Onboarding /></RoleGate>} />
              <Route path="offboarding" element={<RoleGate allow={ADMIN}><Offboarding /></RoleGate>} />
              <Route path="assets" element={<Assets />} />
              <Route path="documents" element={<Documents />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="messaging" element={<Messaging />} />
              <Route path="ai" element={<AIAssistant />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="meetings" element={<Meetings />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="helpdesk" element={<Helpdesk />} />
              <Route path="claims" element={<Claims />} />
              <Route path="performance" element={<Performance />} />
              <Route path="recruitment" element={<RoleGate allow={ADMIN}><Recruitment /></RoleGate>} />
              <Route path="audit-log" element={<RoleGate allow={ADMIN}><AuditLog /></RoleGate>} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="organization" element={<RoleGate allow={ADMIN}><Organization /></RoleGate>} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="feature-controls" element={<RoleGate allow={["super_admin"]}><FeatureControls /></RoleGate>} />
              <Route path="settings" element={<RoleGate allow={["super_admin"]}><Settings /></RoleGate>} />
              <Route path="backup-restore" element={<RoleGate allow={["super_admin"]}><BackupRestore /></RoleGate>} />
              <Route path="communication" element={<RoleGate allow={MANAGER}><CommunicationSuite /></RoleGate>} />
              <Route path="meetings-video" element={<MeetingsVideo />} />
              <Route path="performance-pro" element={<RoleGate allow={MANAGER}><PerformancePro /></RoleGate>} />
              <Route path="recruitment-ats" element={<RoleGate allow={ADMIN}><RecruitmentATS /></RoleGate>} />
              <Route path="lms" element={<LMS />} />
              <Route path="workflow-studio" element={<RoleGate allow={ADMIN}><WorkflowStudio /></RoleGate>} />
              <Route path="ai-copilots" element={<RoleGate allow={MANAGER}><AICopilots /></RoleGate>} />
            </Route>

            {/* ========== PLATFORM CONSOLE — separate layout for platform owners ========== */}
            <Route path="/master" element={<PlatformLayout />}>
              <Route index element={<PlatformDashboard />} />
              <Route path="client-health"  element={<ClientHealth />} />
              <Route path="usage"          element={<UsageAnalytics />} />
              <Route path="tenants"        element={<MasterPanel />} />
              <Route path="tenant-users"   element={<TenantUsers />} />
              <Route path="demo"           element={<DemoWorkspaces />} />
              <Route path="impersonation"  element={<ImpersonationLogs />} />
              <Route path="pricing"        element={<PricingMgr />} />
              <Route path="subscriptions"  element={<SubscriptionsMgr />} />
              <Route path="invoices"       element={<InvoicesMgr />} />
              <Route path="license-keys"   element={<LicenseKeys />} />
              <Route path="trials"         element={<TrialMgmt />} />
              <Route path="feature-controls" element={<FeatureControlsMaster />} />
              <Route path="module-access"   element={<ModuleAccess />} />
              <Route path="brand-content"  element={<BrandContentManager />} />
              <Route path="widget-manager" element={<WidgetManager />} />
              <Route path="website"        element={<WebsiteManager />} />
              <Route path="email-pdf"      element={<EmailPdfTemplates />} />
              <Route path="backups"        element={<BackupRestore />} />
              <Route path="system-health"  element={<SystemHealth />} />
              <Route path="api-usage"      element={<ApiUsage />} />
              <Route path="storage-usage"  element={<StorageUsage />} />
              <Route path="error-logs"     element={<ErrorLogs />} />
              <Route path="audit-logs"     element={<AuditLogsMaster />} />
              <Route path="security-logs"  element={<SecurityLogs />} />
              <Route path="tickets"        element={<SupportTickets />} />
              <Route path="client-requests" element={<ClientRequests />} />
              <Route path="announcements"  element={<ClientAnnouncements />} />
              <Route path="settings"       element={<PlatformSettings />} />
              <Route path="admin-users"    element={<AdminUsers />} />
              <Route path="roles"          element={<RolesPermissions />} />
              <Route path="integrations"   element={<Integrations />} />
            </Route>
            <Route path="/plans-admin"      element={<PlatformLayout />}>
              <Route index element={<PlansAdmin />} />
            </Route>
            <Route path="/platform-billing" element={<PlatformLayout />}>
              <Route index element={<PlatformBilling />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
        </BCMProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}
